import { configureStore, combineReducers } from '@reduxjs/toolkit';
// import storage from 'redux-persist/lib/storage';
import { persistReducer, persistStore, createTransform } from 'redux-persist';
import storageSession from 'redux-persist/lib/storage/session';
import { statusOptions, FEATURE_NAMES, appFlowDictionary } from '../common/constants';
import type { AdvisorConsoleStore } from '../features/advisorConsole/types';
import globalReducer from '../features/global/globalSlice';
import authReducer from '../features/auth/authSlice';
import advisorAdmin from '../features/adviceAdmin';
import adviceClient from '../features/client';
import adviceClientAccounts from '../features/client-accounts';
import adviceGoals from '../features/client-goals';
import ews from '../features/ews';
import unsavedPlanChanges from '../features/unsaved-plan-changes';
import advisorConsole from '../features/advisorConsole';
import recentUpdatesReducer from '../features/recentUpdates';
import appLoaderReducer from '../features/global/appLoader';
import profileEditReducer from '../features/global/profileEditSlice';
import goalEditReducer from '../features/global/goalEditSlice';

const DEFAULT_STATUS_FILTER = statusOptions[2]?.label;

const FLOW2_FEATURE_FLAG = FEATURE_NAMES['DA_FLOW2_FE_AEX-2751_AEX-2626'];

// Determine whether the persisted session belongs to a flow2 tenant. We can't rely on the store or
// getClientJourneyFlowType here: this module creates the store (so it doesn't exist yet), and
// utils -> store would be a circular import. Instead we derive it from the raw persisted slices,
// which redux-persist hands to the transform's outbound function at rehydrate time. `rawState`
// maps each persisted slice key to its serialized JSON string, so auth (tenant) and global
// (featureFlags) are parsed individually. Mirrors getClientJourneyFlowType's flow2 check.
const isPersistedSessionFlow2 = (rawState: Record<string, string> | undefined): boolean => {
    try {
        const auth = rawState?.auth ? JSON.parse(rawState.auth) : undefined;
        const global = rawState?.global ? JSON.parse(rawState.global) : undefined;
        const tenant: string | undefined = auth?.user?.tenant;
        const featureFlags: string[] = global?.featureFlags ?? [];
        if (!tenant || !featureFlags.includes(FLOW2_FEATURE_FLAG)) return false;
        const isFlow2 = (appFlowDictionary.flow2 ?? []).includes(tenant);
        return isFlow2;
    } catch {
        return false;
    }
};

// Reset only the advisor-console status filter on rehydration (page/component reload). State is
// persisted to sessionStorage as-is (inbound); on the way back out we restore the default status so
// a reload never carries over a previously selected status filter. Scoped to the advisorConsole
// slice via `whitelist`, and only applied for flow2 tenants (whose default status is "Complete").
const resetStatusFilterOnRehydrate = createTransform<AdvisorConsoleStore, AdvisorConsoleStore>(
    (inboundState) => inboundState,
    (outboundState, _key, rawState) => {
        if (!outboundState?.filters) return outboundState;
        if (!isPersistedSessionFlow2(rawState as Record<string, string> | undefined)) return outboundState;
        return {
            ...outboundState,
            filters: { ...outboundState.filters, status: DEFAULT_STATUS_FILTER }
        };
    },
    { whitelist: ['advisorConsole'] }
);

const persistConfig = {
    key: 'root',
    storage: storageSession,
    // Transient inline create/edit UI state must always start clean; never rehydrate it, otherwise a
    // mid-edit refresh leaves editingRowKey / edit-open flags stale and locks navigation. goalEdit
    // holds the inline goal edit state; profileEdit holds the profile/account edit-open + dirty flags.
    blacklist: ['goalEdit', 'profileEdit'],
    // The advisor-console status filter must not survive a page/component reload: on rehydrate we
    // drop the persisted `filters.status` back to its default. The transform itself only acts for
    // flow2 sessions, so it's safe to always register.
    transforms: [resetStatusFilterOnRehydrate]
};

const reducers = combineReducers({
    global: globalReducer,
    auth: authReducer,
    advisorAdmin,
    adviceClient,
    adviceClientAccounts,
    adviceGoals,
    ews,
    unsavedPlanChanges,
    advisorConsole,
    recentUpdates: recentUpdatesReducer,
    appLoader: appLoaderReducer,
    profileEdit: profileEditReducer,
    goalEdit: goalEditReducer
});

const persistedReducer = persistReducer<ReturnType<typeof reducers>>(persistConfig, reducers);

const store = configureStore({
    reducer: persistedReducer,
    devTools: process.env.NODE_ENV !== 'production',
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false,
            immutableCheck: false
        })
});

export const persistor = persistStore(store);
export default store;
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
